import React from 'react';
import { Route, Redirect, IndexRedirect } from 'react-router';
import Loadable from 'react-loadable';
import Spinner from '../Spinner';

import StationMetaData from './MetaData/StationMetaData';
import StationMetaDataTemplate from './MetaDataTemplate/StationMetaDataTemplate';
import StationMetaDataAudit from "./MetaData/StationMetaDataAudit";
import StationMetaDataAuditList from "./MetaData/StationMetaDataAuditList";
import StationDataOriginList from './DataOrigin/StationDataOriginList';
import StationDataOriginAdd from './DataOrigin/StationDataOriginAdd';
import StationDataOriginEdit from './DataOrigin/StationDataOriginEdit';
import StationDataSeries from './DataSeries/StationDataSeries';
import StationObservationList from './Observation/StationObservationList';
import StationDataSeriesAdd from './DataSeries/StationDataSeriesAdd';
import StationDataSeriesEdit from './DataSeries/StationDataSeriesEdit';
import StationDataSeriesQC from './DataSeries/StationDataSeriesQC2';
import StationFullObservation from "./FullObservation/StationFullObservation";
import AggregationAdd from "./Aggregation/AggregationAdd";
import AggregationEdit from "./Aggregation/AggregationEdit";
import StationManualAggregation from "./Aggregation/StationManualAggregation";
import StationQcCheckList from './QcCheck/StationQcCheckList';
import StationManualQc from "./QcCheck/StationManualQc";
import StationQcCheckList2 from './QcCheck/StationQcCheckList2';
import EditObservation from './EditObservation/EditObservation';
import StationMonitoringList from './Monitoring/StationMonitoringList';

import StationTicketList from "./Maintenance/Ticket/StationTicketList";
import StationWorkOrderList from "./Maintenance/WorkOrder/StationWorkOrderList";
import StationMaintenance from "./Maintenance/StationMaintenance";
import TicketAdd from '../Maintenance/Ticket/TicketAdd';
import TicketEdit from "../Maintenance/Ticket/TicketEdit";
import TicketView from "../Maintenance/Ticket/TicketView";
import WorkOrderAdd from "../Maintenance/WorkOrder/WorkOrderAdd";
import WorkOrderEdit from "../Maintenance/WorkOrder/WorkOrderEdit";
import WorkOrderView from "../Maintenance/WorkOrder/WorkOrderView";
import WorkOrderLogRoot from "../Maintenance/WorkOrder/Log/WorkOrderLogRoot";
import WorkOrderTaskList from "../Maintenance/WorkOrder/Task/WorkOrderTaskList";
import WorkOrderTaskAdd from "../Maintenance/WorkOrder/Task/WorkOrderTaskAdd";
import WorkOrderTaskEdit from "../Maintenance/WorkOrder/Task/WorkOrderTaskEdit";
import TicketWorkOrderAdd from "../Maintenance/Ticket/TicketWorkOrderAdd";
import WorkOrderTicketAdd from "../Maintenance/WorkOrder/WorkOrderTicketAdd";
import WorkOrderStationAdd from "../Maintenance/WorkOrder/WorkOrderStationAdd";

import InventoryItemMovementAdd from '../Inventory/ItemMovementAdd';
import ItemRelatedFileAdd from '../Inventory/ItemRelatedFileAdd';
import StationInventoryList from './Inventory/StationInventoryList';
import StationInventoryModify from './Inventory/StationInventoryModify';

import StationImageRoute from "./Image/routes";
import StationLocationMap from "./Location/StationLocationMap2";
import StationTypeModify from './Type/StationTypeModify';
import LocationModify from '../Location/LocationModify';

// import StationFtpHome from "./Ftp/StationFtpHome";
// import StationFtpView from "./Ftp/StationFtpView";
// import StationFtpConfiguration from "./Ftp/StationFtpConfiguration";

import { checkRolePermission, checkStationPermission } from "../../libs/checkPermission";

const Station = Loadable({
  loader: () => import('./Station'),
  loading: () => <Spinner />
});

export const StationRoute = () => (
  <Route path="station/:id" component={checkStationPermission(Station)}>
    <IndexRedirect to='meta-data' />
    <Route path="meta-data" component={StationMetaData}>
      <Route path='location/:locationId' component={LocationModify} />
      <Route path='station-type/:typeId' component={StationTypeModify} />
    </Route>
    <Route path='meta-data-audit' component={StationMetaDataAudit}>
      <Route path=':auditId' component={StationMetaDataAuditList} />
    </Route>

    <Route path="edit-observation" component={checkRolePermission(EditObservation)} name='view edit-observation' />

    <Route path="data-origin" component={checkRolePermission(StationDataOriginList)} name='view data-origin'>
      <Route path="add" component={checkRolePermission(StationDataOriginAdd)} name='modify data-origin' />
      <Route path=":dataOriginId" component={checkRolePermission(StationDataOriginEdit)} name='modify data-origin' />
    </Route>

    <Route path="monitoring" component={StationMonitoringList} />

    <Route path="data-series" component={checkRolePermission(StationDataSeries)} name="view data-series">
      <Route path="add" component={checkRolePermission(StationDataSeriesAdd)} name='modify data-series' />
      <Route path=":dataSeriesId" component={checkRolePermission(StationDataSeriesEdit)} name='modify data-series' />
      <Route path=":dataSeriesId/qc-rule" component={checkRolePermission(StationDataSeriesQC)} name='modify qc-rule' />
      <Redirect from=":dataSeriesId/qc-rule/:qcRuleId" to="/qc-rule/:qcRuleId" />
    </Route>

    <Route path="observation" component={StationObservationList} />
    <Route path="full-observation" component={StationFullObservation} />
    <Route path="aggregation" component={StationManualAggregation}>
      <Route path="add" component={AggregationAdd} />
      <Route path=":aggregation_id" component={AggregationEdit} />
    </Route>

    <Route path="inventory" component={checkRolePermission(StationInventoryList)} name='view inventory'>
      <Route path="item">
        <Route path=":itemId" component={checkRolePermission(StationInventoryModify)} name='modify inventory' />
      </Route>
      <Route path="item-movement/:itemId">
        <Route path="add" component={checkRolePermission(InventoryItemMovementAdd)} name='modify inventory' />
        <Route path="edit" component={checkRolePermission(StationInventoryModify)} name='modify inventory' />
      </Route>
      <Route path="related-files/:itemId">
        <Route path="add" component={checkRolePermission(ItemRelatedFileAdd)} name='modify inventory' />
        <Route path="edit" component={checkRolePermission(StationInventoryModify)} name='modify inventory' />
      </Route>
    </Route>

    <Route path="qc-check">
      <IndexRedirect to='recent' />
      <Route path='recent' component={checkRolePermission(StationQcCheckList)} name='view qc-check' />
      <Route path='view' component={checkRolePermission(StationQcCheckList2)} name='view qc-check' />
      <Route path='manual' component={checkRolePermission(StationManualQc)} name='modify qc-check' />
    </Route>

    {StationImageRoute()}

    <Route path="meta-data" component={StationMetaData}>
      <Route path="template" component={StationMetaDataTemplate} />
    </Route>

    <Route path='maintenance' component={StationMaintenance}>
      <IndexRedirect to='ticket' />
      <Route path='ticket' component={StationTicketList}>
        <Route path='add' component={TicketAdd} />
        <Route path=':ticket_id' component={TicketEdit} />
        <Route path=':ticket_id/view' components={{ ticketView: TicketView }} >
          <Route path='work_order/add' component={TicketWorkOrderAdd} />
        </Route>
      </Route>
      <Route path='work_order' component={StationWorkOrderList}>
        <Route path='add' component={WorkOrderAdd} />
        <Route path=':work_order_id' component={WorkOrderEdit} />
        <Route path=':work_order_id/view' components={{ workOrderView: WorkOrderView }} >
          <Route path='ticket/add' component={WorkOrderTicketAdd} />
          <Route path='station/add' component={WorkOrderStationAdd} />
          <IndexRedirect to='logs' />
          <Route path='logs' component={WorkOrderLogRoot} />
          <Route path='tasks' component={WorkOrderTaskList}>
            <Route path='add' component={WorkOrderTaskAdd} />
            <Route path=':task_id' component={WorkOrderTaskEdit} />
          </Route>
        </Route>
      </Route>
    </Route>

    <Route path='location' component={StationLocationMap}>

    </Route>

    {/*<Route path='ftp' component={StationFtpHome}>*/}
    {/*  <IndexRedirect to='view'/>*/}
    {/*  <Route path='view' component={StationFtpView}/>*/}
    {/*  <Route path='configuration' component={StationFtpConfiguration}/>*/}
    {/*</Route>*/}

  </Route>
);


// WEBPACK FOOTER //
// ./src/components/Station/routes.js